import React, { useState, useRef, useEffect } from 'react';
import { Code, Download, RotateCcw, ChevronDown, Clock } from 'lucide-react';
import ThemeToggle from './ThemeToggle';

function Toolbar({ 
  onExportClick,
  onReset,
  isGenerating,
  showCode,
  onToggleCode,
  versionHistory = [],
  onSelectVersion
}) {
  const [showVersionDropdown, setShowVersionDropdown] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowVersionDropdown(false);
      }
    }
    
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Format timestamp for display
  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="bg-white dark:bg-dark-bg-secondary border-b border-gray-200 dark:border-dark-border p-3 flex justify-between items-center shrink-0">
      <div className="flex items-center gap-4">
        <div className="font-medium dark:text-dark-text-primary">Project Dashboard</div> 
        <div className="text-gray-500 dark:text-dark-text-secondary">•</div>
        <div className="flex items-center relative" ref={dropdownRef}>
          <button 
            onClick={() => setShowVersionDropdown(!showVersionDropdown)}
            disabled={versionHistory.length === 0}
            className={`flex items-center gap-1 px-2 py-1 rounded transition-colors text-sm ${
              versionHistory.length === 0 
                ? 'text-gray-400 dark:text-gray-500 cursor-not-allowed' 
                : 'text-gray-600 dark:text-dark-text-secondary hover:bg-gray-100 dark:hover:bg-dark-bg-primary'
            }`}
          >
            <span>Version history</span>
            <ChevronDown size={14} />
          </button>
          
          {showVersionDropdown && versionHistory.length > 0 && (
            <div className="absolute top-full left-0 mt-1 w-64 bg-white dark:bg-dark-bg-secondary border border-gray-200 dark:border-dark-border shadow-lg rounded-md z-50">
              <div className="p-2 border-b border-gray-100 dark:border-dark-border text-sm font-medium text-gray-700 dark:text-dark-text-primary flex items-center">
                <Clock size={14} className="mr-2" />
                <span>Recent versions</span>
              </div>
              <div className="max-h-80 overflow-y-auto">
                {versionHistory.map((version, index) => (
                  <button
                    key={version.id}
                    onClick={() => {
                      onSelectVersion(version.id);
                      setShowVersionDropdown(false);
                    }}
                    className={`w-full text-left p-2 text-sm hover:bg-gray-50 dark:hover:bg-dark-bg-primary flex items-center justify-between ${
                      index === 0 ? 'text-blue-600 dark:text-dark-accent font-medium' : 'text-gray-700 dark:text-dark-text-primary'
                    }`}
                  >
                    <div>
                      <div className="font-medium">{formatTime(version.timestamp)}</div>
                      <div className="text-xs text-gray-500 dark:text-dark-text-secondary truncate" style={{ maxWidth: '180px' }}>
                        {version.description || 'Design update'}
                      </div>
                    </div>
                    {index === 0 && <div className="text-xs text-blue-600 dark:text-dark-accent shrink-0">Current</div>}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      
      <div className="flex items-center gap-2">
        <ThemeToggle />
        <button 
          onClick={onToggleCode} 
          className="px-3 py-1 text-sm bg-white dark:bg-dark-bg-primary border border-gray-200 dark:border-dark-border rounded hover:bg-gray-50 dark:hover:bg-dark-bg-secondary flex items-center gap-2 dark:text-dark-text-primary"
          disabled={isGenerating}
        >
          <Code size={16} />
          {showCode ? 'Preview' : 'Code'}
        </button>
        <button 
          onClick={onReset}
          className="px-3 py-1 text-sm bg-white dark:bg-dark-bg-primary border border-gray-200 dark:border-dark-border rounded hover:bg-gray-50 dark:hover:bg-dark-bg-secondary flex items-center gap-2 dark:text-dark-text-primary"
          disabled={isGenerating}
        >
          <RotateCcw size={16} />
          <span>Reset</span>
        </button>
        <button 
          onClick={onExportClick}
          className="px-3 py-1 text-sm bg-black dark:bg-dark-accent text-white rounded hover:bg-gray-800 dark:hover:bg-blue-700 flex items-center gap-2"
          disabled={isGenerating}
        >
          <Download size={16} />
          <span>Export</span>
        </button>
      </div>
    </div>
  );
}

export default Toolbar; 